import React, { useEffect, useRef, useCallback } from 'react';

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // Center of US
const DEFAULT_ZOOM = 4;
const HEATMAP_ZOOM_THRESHOLD = 14; // Switch from heatmap to individual pins at this zoom

/**
 * Lightweight Google Maps wrapper with a dual-mode view:
 *  - Zoomed out: heatmap layer showing address density
 *  - Zoomed in (≥14): individual pins with click → table sync
 */
const TargetingAddressMap = ({ locations = [], google, onPinClick, highlightedId }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const heatmapRef = useRef(null);
  const markersRef = useRef([]);
  const listenerRef = useRef(null);

  // Initialize map
  useEffect(() => {
    if (!google || !mapRef.current) return;

    mapInstanceRef.current = new google.maps.Map(mapRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }],
        },
      ],
    });
  }, [google]);

  const handleMarkerClick = useCallback(
    (location) => {
      if (onPinClick) {
        onPinClick(location);
      }
    },
    [onPinClick]
  );

  const getValidLocations = useCallback(() => {
    return locations.filter(
      (loc) => loc.lat && loc.lng && !isNaN(loc.lat) && !isNaN(loc.lng)
    );
  }, [locations]);

  // Clear all markers
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
  }, []);

  // Render individual pin markers
  const renderPinMarkers = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!google || !map) return;

    clearMarkers();

    getValidLocations().forEach((loc) => {
      const position = { lat: parseFloat(loc.lat), lng: parseFloat(loc.lng) };
      const isHighlighted = highlightedId === loc.id;

      const marker = new google.maps.Marker({
        position,
        map,
        title: loc.name || loc.formattedAddress || '',
        icon: isHighlighted
          ? {
              url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
              scaledSize: new google.maps.Size(40, 40),
            }
          : undefined,
      });

      marker.addListener('click', () => handleMarkerClick(loc));
      markersRef.current.push(marker);
    });
  }, [google, getValidLocations, highlightedId, handleMarkerClick, clearMarkers]);

  // Switch between heatmap and pins based on zoom
  const updateView = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!google || !map) return;

    const zoom = map.getZoom();
    const showPins = zoom >= HEATMAP_ZOOM_THRESHOLD;

    // Toggle heatmap
    if (heatmapRef.current) {
      heatmapRef.current.setMap(showPins ? null : map);
    }

    // Toggle pins
    if (showPins) {
      renderPinMarkers();
    } else {
      clearMarkers();
    }
  }, [google, renderPinMarkers, clearMarkers]);

  // Build heatmap + fit bounds when locations change
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!google || !map) return;

    // Tear down old heatmap
    if (heatmapRef.current) {
      heatmapRef.current.setMap(null);
      heatmapRef.current = null;
    }
    clearMarkers();

    const valid = getValidLocations();
    if (valid.length === 0) return;

    // Fit bounds
    const bounds = new google.maps.LatLngBounds();
    const heatmapData = valid.map((loc) => {
      const latLng = new google.maps.LatLng(parseFloat(loc.lat), parseFloat(loc.lng));
      bounds.extend(latLng);
      return latLng;
    });

    if (valid.length === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(14);
    } else {
      map.fitBounds(bounds, { padding: 50 });
    }

    // Create heatmap layer
    heatmapRef.current = new google.maps.visualization.HeatmapLayer({
      data: heatmapData,
      map,
      radius: 25,
      opacity: 0.7,
      gradient: [
        'rgba(0, 0, 0, 0)',
        'rgba(0, 228, 0, 0.4)',
        'rgba(255, 255, 0, 0.6)',
        'rgba(255, 165, 0, 0.8)',
        'rgba(255, 69, 0, 0.9)',
        'rgba(220, 20, 20, 1)',
      ],
    });

    updateView();
  }, [locations, google, getValidLocations, clearMarkers, updateView]);

  // Re-render on highlighted change (pins mode only)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!google || !map) return;
    if (map.getZoom() >= HEATMAP_ZOOM_THRESHOLD) {
      renderPinMarkers();
    }
  }, [highlightedId, google, renderPinMarkers]);

  // Listen to zoom changes to toggle modes
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!google || !map) return;

    if (listenerRef.current) {
      google.maps.event.removeListener(listenerRef.current);
    }

    listenerRef.current = map.addListener('zoom_changed', () => {
      updateView();
    });

    return () => {
      if (listenerRef.current) {
        google.maps.event.removeListener(listenerRef.current);
      }
    };
  }, [google, updateView]);

  if (!google) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 400,
          color: '#9e9e9e',
          fontSize: 13,
        }}
      >
        Loading map...
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 400 }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%', minHeight: 400 }} />
      <div
        style={{
          position: 'absolute',
          bottom: 12,
          left: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'rgba(255,255,255,0.92)',
          borderRadius: 6,
          padding: '5px 10px',
          fontSize: 11,
          color: '#444',
          boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
          pointerEvents: 'none',
        }}
      >
        <span>Low</span>
        <div
          style={{
            width: 100,
            height: 10,
            borderRadius: 4,
            background: 'linear-gradient(to right, #00e400, #ffff00, #ffa500, #ff4500, #dc1414)',
          }}
        />
        <span>High</span>
      </div>
    </div>
  );
};

export default TargetingAddressMap;
